import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
} from 'recharts';
import { rangeStats, StatRows, fmtPct, pctColor } from './rangeStats';

const fmtUSD = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function IndexStatsCard({ data, dateRange }) {
  const slice = useMemo(() => {
    const history = data?.history || [];
    if (!history.length) return [];
    if (!dateRange?.start || !dateRange?.end) return history;
    return history.filter((b) => b.date >= dateRange.start && b.date <= dateRange.end);
  }, [data, dateRange]);

  const stats = useMemo(() => rangeStats(slice, 'close', data?.current), [slice, data]);

  if (!data || data.error) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full">
        <h3 className="text-sm font-semibold text-gray-300">{data?.label || data?.ticker || 'Index'}</h3>
        <p className="text-gray-500 text-sm mt-2">{data?.error || 'Unavailable'}</p>
      </div>
    );
  }

  const { label, ticker, current } = data;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 h-full flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-300">{label || ticker}</h3>
          <span className="text-3xl font-bold text-white">{fmtUSD(current)}</span>
          {stats && (
            <span className={`text-sm font-semibold ${pctColor(stats.changePct)}`}>
              Range {fmtPct(stats.changePct)}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">{ticker}</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 flex-1">
        <div className="sm:w-44 flex-shrink-0">
          <StatRows stats={stats} fmtValue={fmtUSD} />
        </div>
        <div className="flex-1 min-h-[140px]">
          <ResponsiveContainer width="100%" height="100%" minHeight={140}>
            <LineChart data={slice} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 9 }}
                minTickGap={40}
              />
              <YAxis domain={['auto', 'auto']} hide />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [fmtUSD(v), 'Close']}
              />
              <Line type="monotone" dataKey="close" stroke="#60a5fa" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="text-[10px] text-gray-500 mt-2">ETF proxy · daily closes · high/low are close-based</div>
    </div>
  );
}

export default IndexStatsCard;
